import { Alert, Form, Modal } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { TextInput } from "../../../../components/Input/TextInput.tsx"

import { folderNameProblem } from "../../helpers/reportFolders.ts"
import { FOLDER_MAX_LENGTH } from "../../helpers/reportSettings.ts"

interface ReportFolderNameModalProps {
  mode: "create" | "rename"
  /** The folder being renamed; absent when creating. */
  current?: string
  existing: string[]
  /** Resolves to null on success, or the server's reason for refusing. */
  onSubmit: (name: string) => Promise<string | null>
  onClose: () => void
}

/**
 * Names a folder, for both New folder and Rename folder. A refused request
 * keeps the dialog open with the typed name, so the fix is one edit away.
 */
export const ReportFolderNameModal = ({
  mode,
  current,
  existing,
  onSubmit,
  onClose
}: ReportFolderNameModalProps) => {
  const { t } = useTranslation()
  const [name, setName] = useState(current ?? "")
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  const problem = folderNameProblem(name, existing, current)
  const fieldError =
    touched && problem ? t(`reports.folders.errors.${problem}`) : null

  const handleOk = async () => {
    setTouched(true)
    if (problem || saving) return
    if (mode === "rename" && name.trim() === current) {
      onClose()
      return
    }
    setSaving(true)
    setServerError(null)
    try {
      const refusal = await onSubmit(name.trim())
      if (refusal === null) onClose()
      else setServerError(refusal || t("reports.folders.errors.saveFailed"))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      centered
      maskClosable={false}
      destroyOnHidden
      width={440}
      title={t(
        mode === "create"
          ? "reports.folders.createTitle"
          : "reports.folders.renameTitle"
      )}
      okText={t(
        mode === "create" ? "reports.folders.create" : "reports.folders.rename"
      )}
      cancelText={t("reports.cancel")}
      confirmLoading={saving}
      cancelButtonProps={{ disabled: saving }}
      onOk={handleOk}
      onCancel={() => !saving && onClose()}
    >
      <Form
        layout="vertical"
        onFinish={handleOk}
        className="with-default-error pt-2"
      >
        {serverError && (
          <Alert type="error" showIcon message={serverError} className="mb-3" />
        )}
        <Form.Item
          required
          label={t("reports.folders.nameLabel")}
          validateStatus={fieldError ? "error" : undefined}
          help={fieldError ?? undefined}
        >
          <TextInput
            autoFocus
            disableAutoCapitalize
            aria-label={t("reports.folders.nameLabel")}
            value={name}
            maxLength={FOLDER_MAX_LENGTH}
            placeholder={t("reports.folders.namePlaceholder")}
            onChange={(e) => {
              setName(e.target.value)
              setServerError(null)
            }}
            onBlur={() => setTouched(true)}
            onPressEnter={handleOk}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
